/**
 * ABAC Policies Page (/admin/abac-policies)
 *
 * Tenant-scoped CRUD surface for attribute-based access control policies
 * authored by FI tenant admins. Policies use a LOCKED-SUBSET JsonLogic
 * predicate dialect sealed in Sprint 4 Lane C: only the operators below
 * are permitted at any node in the predicate tree.
 *
 *   Comparators: ==, !=, <, <=, >, >=
 *   Logical:     all, any, not
 *   Membership:  in
 *   Operand:     var
 *
 * Sprint 1 deviation #3: AbacPolicy has NO displayName; the `name` field
 * doubles as slug + label.
 *
 * Backend (all paths /api/v1/...):
 *   GET    /api/v1/admin/abac-policies
 *   POST   /api/v1/admin/abac-policies
 *   PUT    /api/v1/admin/abac-policies/{abacPolicyId}
 *   DELETE /api/v1/admin/abac-policies/{abacPolicyId}
 *   POST   /api/v1/admin/roles/{roleId}/abac-policies
 *   DELETE /api/v1/admin/roles/{roleId}/abac-policies/{abacPolicyId}
 *   POST   /api/v1/admin/staff/{userId}/role-abac-overrides
 *
 * The spec intentionally omits a role-attachment GET and a user-override
 * GET (the server-side effective-policy resolution is exercised through
 * /api/v1/admin/abac/check). The Role Attachments and Per-User Overrides
 * panels below track attach/detach actions performed during the current
 * session only -- a future spec extension could add list endpoints to
 * populate them on page load.
 *
 * S3F (2026-05-22): the role-centric attach/detach handlers send the
 * Role row UUID (NOT the role name string) as the {roleId} path param.
 * Roles are loaded via platformAdminClient.GET('/users/roles') which
 * proxies to aaim-core authorization's role catalog. The prior wiring
 * sent literal 'fi_admin' / 'fi_loan_officer' / 'fi_compliance' strings
 * which the spec-canonical handler at
 * staging/aaim-core-new/services/authorization/src/routes/
 * abac-policies.routes.ts rejects with 400 VALIDATION_ERROR (roleId
 * must be a UUID).
 *
 * Per-user override path-param `userId` is the TenantUser row id. The
 * `userRoleId` in the body is the UserRole join-row id. Neither id is
 * surfaced by the existing fi-staff-lifecycle staff-list endpoint
 * (RoleRef carries only Role.id), so this surface accepts raw UUIDs
 * until a follow-on extension surfaces UserRole.id on the staff list.
 *
 * Types: components['schemas']['AbacPolicy'] etc. from
 *        @aaimplatform/institutional-types/abac-policies
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Heading,
  BodyText,
  Button,
  Badge,
  DataTable,
  type DataTableColumn,
  EmptyState,
  SectionLabel,
  Sheet,
  SkeletonTable,
  Textarea,
  Select,
  type SelectOption,
  Alert,
  Input,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/abac-policies';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/platform-admin';
import { useAuthStore } from '../../stores/auth';
import { abacPoliciesClient, platformAdminClient } from '../../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AbacPolicy = components['schemas']['AbacPolicy'];
type RoleSummary = PlatformAdminComponents['schemas']['RoleSummary'];

interface RoleAttachment {
  roleId: string;
  roleName: string;
  policy: AbacPolicy;
}

// ---------------------------------------------------------------------------
// Locked-subset validator (Sprint 4 Lane C canonical operator set)
// ---------------------------------------------------------------------------

/**
 * The only operator keys permitted at any single-key object node in a
 * JsonLogic predicate. Authored from the canonical evaluator at
 * staging/aaim-core-new/packages/auth/src/abac/evaluator.ts. Any addition
 * here MUST be paired with a server-side change in the canonical evaluator
 * AND an explicit ADR. Refuse to add operators outside this set.
 */
const PERMITTED_OPERATORS = new Set<string>([
  '==',
  '!=',
  '<',
  '<=',
  '>',
  '>=',
  'all',
  'any',
  'not',
  'in',
  'var',
]);

/**
 * UUID v4 / v1 / v5 regex. Matches the canonical 36-character form. Used
 * for client-side validation of the per-user override UUIDs before the
 * request fires; the backend re-validates with an identical regex
 * (UUID_RE in abac-policies.routes.ts).
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ValidationFailure {
  path: string;
  operator: string;
}

/**
 * Recursive validator. Walks the predicate tree, asserts every single-key
 * object's key is in PERMITTED_OPERATORS. Returns the first violation or
 * null if the entire tree is in-subset.
 */
function validatePredicate(node: unknown, path: string = '$'): ValidationFailure | null {
  if (node === null || node === undefined) return null;
  if (typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const result = validatePredicate(node[i], `${path}[${i}]`);
      if (result) return result;
    }
    return null;
  }
  const keys = Object.keys(node);
  // JsonLogic single-key object form: { "==": [...] }, { "var": "x" }, etc.
  if (keys.length === 1) {
    const op = keys[0];
    if (!PERMITTED_OPERATORS.has(op)) {
      return { path, operator: op };
    }
    const child = (node as Record<string, unknown>)[op];
    return validatePredicate(child, `${path}.${op}`);
  }
  // Multi-key objects are operand bags (e.g., a context shape). Recurse
  // into each value but do not assert key membership against the operator
  // allowlist -- those keys are data field names, not operators.
  for (const k of keys) {
    const result = validatePredicate(
      (node as Record<string, unknown>)[k],
      `${path}.${k}`,
    );
    if (result) return result;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Client-side preview evaluator
//
// Mirrors the locked-subset semantics. Production evaluation runs server-side
// at /api/v1/abac/check (caller-scoped against effective policies); this
// client evaluator is for predicate-authoring preview only.
// ---------------------------------------------------------------------------

interface EvalResult {
  allowed: boolean;
  reason?: string;
}

function evalNode(node: unknown, ctx: Record<string, unknown>): unknown {
  if (node === null || node === undefined) return node;
  if (typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map((n) => evalNode(n, ctx));

  const keys = Object.keys(node);
  if (keys.length !== 1) return node;
  const op = keys[0];
  const args = (node as Record<string, unknown>)[op];

  if (op === 'var') {
    const path = typeof args === 'string' ? args : '';
    if (!path) return ctx;
    return path.split('.').reduce<unknown>((acc, segment) => {
      if (acc && typeof acc === 'object' && segment in (acc as Record<string, unknown>)) {
        return (acc as Record<string, unknown>)[segment];
      }
      return undefined;
    }, ctx);
  }

  const arr = Array.isArray(args) ? args.map((n) => evalNode(n, ctx)) : [evalNode(args, ctx)];

  switch (op) {
    case '==':
      return arr[0] === arr[1];
    case '!=':
      return arr[0] !== arr[1];
    case '<':
      return Number(arr[0]) < Number(arr[1]);
    case '<=':
      return Number(arr[0]) <= Number(arr[1]);
    case '>':
      return Number(arr[0]) > Number(arr[1]);
    case '>=':
      return Number(arr[0]) >= Number(arr[1]);
    case 'all':
      return arr.every((v) => Boolean(v));
    case 'any':
      return arr.some((v) => Boolean(v));
    case 'not':
      return !arr[0];
    case 'in': {
      const needle = arr[0];
      const haystack = arr[1];
      if (Array.isArray(haystack)) return haystack.includes(needle);
      if (typeof haystack === 'string' && typeof needle === 'string') {
        return haystack.includes(needle);
      }
      return false;
    }
    default:
      // Validator should have caught this earlier.
      return false;
  }
}

function runPreview(predicate: unknown, context: Record<string, unknown>): EvalResult {
  try {
    const verdict = evalNode(predicate, context);
    if (verdict === true) {
      return { allowed: true };
    }
    return {
      allowed: false,
      reason: `Predicate evaluated to ${JSON.stringify(verdict)}`,
    };
  } catch (err) {
    return {
      allowed: false,
      reason: err instanceof Error ? err.message : 'Evaluation error',
    };
  }
}

// ---------------------------------------------------------------------------
// Sample-context presets
// ---------------------------------------------------------------------------

interface ContextPreset {
  id: string;
  label: string;
  context: Record<string, unknown>;
}

const CONTEXT_PRESETS: ContextPreset[] = [
  {
    id: 'fi_admin_business_hours',
    label: 'FI Admin during business hours',
    context: {
      role: 'fi_admin',
      hour: 14,
      day: 'tuesday',
      tenantId: 'tenant-001',
    },
  },
  {
    id: 'loan_officer_large_loan',
    label: 'Loan Officer over $1M loan',
    context: {
      role: 'fi_loan_officer',
      loanAmount: 1500000,
      loanCurrency: 'USD',
      tenantId: 'tenant-001',
    },
  },
  {
    id: 'compliance_after_hours',
    label: 'Compliance after-hours',
    context: {
      role: 'fi_compliance',
      hour: 22,
      day: 'saturday',
      tenantId: 'tenant-001',
    },
  },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const STARTER_PREDICATE = JSON.stringify(
  {
    '==': [{ var: 'role' }, 'fi_admin'],
  },
  null,
  2,
);

const STARTER_CONTEXT = JSON.stringify(CONTEXT_PRESETS[0].context, null, 2);

export function AbacPoliciesPage() {
  const { user } = useAuthStore();

  const [policies, setPolicies] = useState<AbacPolicy[]>([]);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [attachments, setAttachments] = useState<RoleAttachment[]>([]);
  // Per-user override session-local state. Shape inlined per
  // typescript-compliance.md (view-model type, not an API contract).
  const [overrides, setOverrides] = useState<
    Array<{ userId: string; userRoleId: string; policy: AbacPolicy }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AbacPolicy | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [predicateText, setPredicateText] = useState(STARTER_PREDICATE);
  const [predicateError, setPredicateError] = useState<string | null>(null);
  const [contextText, setContextText] = useState(STARTER_CONTEXT);
  const [contextError, setContextError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<EvalResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Attach-to-role form state. attachRoleId is the Role row UUID (NOT a
  // role name string); the backend handler at
  // services/authorization/src/routes/abac-policies.routes.ts validates
  // path.roleId against UUID_RE and rejects non-UUID values with 400.
  const [attachRoleId, setAttachRoleId] = useState<string>('');
  const [attachPolicyId, setAttachPolicyId] = useState<string>('');
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  // Per-user override form state. userId is the TenantUser row UUID;
  // userRoleId is the UserRole join-row UUID. Both must reference rows
  // owned by the caller's effective tenant (backend enforces).
  const [overrideUserId, setOverrideUserId] = useState<string>('');
  const [overrideUserRoleId, setOverrideUserRoleId] = useState<string>('');
  const [overridePolicyId, setOverridePolicyId] = useState<string>('');
  const [overriding, setOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const canManage =
    user?.permissions?.includes('abac:manage') ||
    user?.permissions?.includes('roles:manage');

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Parallel fetch: policies (this surface's CRUD target) and roles
      // (needed to resolve role-NAME -> Role.id UUID for the attach/detach
      // path params). Both calls are typed via openapi-fetch.
      const [policiesResult, rolesResult] = await Promise.all([
        abacPoliciesClient.GET('/api/v1/admin/abac-policies'),
        platformAdminClient.GET('/users/roles'),
      ]);

      if (policiesResult.error) {
        throw new Error('Failed to load ABAC policies');
      }
      const loadedPolicies = policiesResult.data?.policies ?? [];
      setPolicies(loadedPolicies);

      if (rolesResult.error) {
        // Roles load failure does not block policy list rendering, but
        // attach/detach UI is disabled until roles are available.
        setRoles([]);
      } else {
        setRoles(rolesResult.data?.data ?? []);
      }

      // Role-attachment GET and per-user-override GET are not in the
      // canonical spec; the panels reflect actions performed in this
      // session only. The session-local lists are pruned whenever the
      // policy list reloads to avoid stale references to deleted policies.
      setAttachments((prev) =>
        prev.filter((a) => loadedPolicies.some((p) => p.id === a.policy.id)),
      );
      setOverrides((prev) =>
        prev.filter((o) => loadedPolicies.some((p) => p.id === o.policy.id)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ABAC data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // -------------------------------------------------------------------------
  // Editor open / close / parse
  // -------------------------------------------------------------------------

  const resetEditor = () => {
    setEditTarget(null);
    setFormName('');
    setFormDescription('');
    setPredicateText(STARTER_PREDICATE);
    setPredicateError(null);
    setContextText(STARTER_CONTEXT);
    setContextError(null);
    setPreviewResult(null);
    setSaveError(null);
  };

  const openCreateEditor = () => {
    resetEditor();
    setEditorOpen(true);
  };

  const openEditEditor = (policy: AbacPolicy) => {
    resetEditor();
    setEditTarget(policy);
    setFormName(policy.name);
    setFormDescription(policy.description ?? '');
    setPredicateText(JSON.stringify(policy.predicateJson ?? {}, null, 2));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    resetEditor();
  };

  const parsedPredicate = useMemo<unknown>(() => {
    try {
      return JSON.parse(predicateText);
    } catch {
      return null;
    }
  }, [predicateText]);

  const validatePredicateText = (): { ok: true; value: unknown } | { ok: false } => {
    try {
      const parsed: unknown = JSON.parse(predicateText);
      const violation = validatePredicate(parsed);
      if (violation) {
        setPredicateError(
          `Predicate rejected: operator '${violation.operator}' is not in the locked subset.`,
        );
        return { ok: false };
      }
      setPredicateError(null);
      return { ok: true, value: parsed };
    } catch (err) {
      setPredicateError(
        err instanceof Error ? `JSON parse error: ${err.message}` : 'JSON parse error',
      );
      return { ok: false };
    }
  };

  const handlePredicateBlur = () => {
    if (!predicateText.trim()) {
      setPredicateError('Predicate is required.');
      return;
    }
    validatePredicateText();
  };

  const isFormValid = (): boolean => {
    if (!editTarget && !formName.trim()) return false;
    if (formDescription.length > 1000) return false;
    if (predicateError) return false;
    if (parsedPredicate === null) return false;
    if (validatePredicate(parsedPredicate) !== null) return false;
    return true;
  };

  // -------------------------------------------------------------------------
  // Save (create / update)
  // -------------------------------------------------------------------------

  const handleSave = async () => {
    const validation = validatePredicateText();
    if (!validation.ok) return;

    setSaving(true);
    setSaveError(null);
    try {
      if (editTarget) {
        const result = await abacPoliciesClient.PUT(
          '/api/v1/admin/abac-policies/{abacPolicyId}',
          {
            params: { path: { abacPolicyId: editTarget.id } },
            body: {
              description: formDescription.trim() || undefined,
              predicateJson: validation.value,
            },
          },
        );
        if (result.error) throw new Error('Failed to update policy');
      } else {
        const trimmedName = formName.trim();
        if (!trimmedName) {
          setSaveError('Name is required.');
          setSaving(false);
          return;
        }
        const result = await abacPoliciesClient.POST('/api/v1/admin/abac-policies', {
          body: {
            name: trimmedName,
            description: formDescription.trim() || undefined,
            predicateJson: validation.value,
          },
        });
        if (result.error) throw new Error('Failed to create policy');
      }
      closeEditor();
      await loadData();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policy: AbacPolicy) => {
    if (!canManage) return;
    setError(null);
    try {
      const result = await abacPoliciesClient.DELETE(
        '/api/v1/admin/abac-policies/{abacPolicyId}',
        { params: { path: { abacPolicyId: policy.id } } },
      );
      if (result.error) throw new Error('Failed to delete policy');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete policy');
    }
  };

  // -------------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------------

  const handleRunPreview = () => {
    setPreviewResult(null);
    setContextError(null);

    // The predicate field sits above the preview in the editor, so its
    // validation error is in view without switching panels.
    const validation = validatePredicateText();
    if (!validation.ok) return;

    let context: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(contextText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setContextError('Context must be a JSON object.');
        return;
      }
      context = parsed as Record<string, unknown>;
    } catch (err) {
      setContextError(
        err instanceof Error ? `JSON parse error: ${err.message}` : 'JSON parse error',
      );
      return;
    }

    setPreviewResult(runPreview(validation.value, context));
  };

  const applyContextPreset = (presetId: string) => {
    const preset = CONTEXT_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setContextText(JSON.stringify(preset.context, null, 2));
    setPreviewResult(null);
    setContextError(null);
  };

  // -------------------------------------------------------------------------
  // Attach / detach
  // -------------------------------------------------------------------------

  /**
   * Surface the server-side ErrorResponse body when present (the canonical
   * shape is { error: string; message: string }; fallback to a generic
   * label when the body is not in that shape). Keeps 4xx responses visible
   * in the UI instead of swallowing them under a generic "Failed" string.
   */
  const formatApiError = (
    result: { error?: unknown },
    fallback: string,
  ): string => {
    const body = result.error as
      | { error?: string; message?: string }
      | undefined;
    if (body && typeof body === 'object') {
      const code = typeof body.error === 'string' ? body.error : undefined;
      const message =
        typeof body.message === 'string' ? body.message : undefined;
      if (code && message) return `${code}: ${message}`;
      if (message) return message;
      if (code) return code;
    }
    return fallback;
  };

  const handleAttach = async () => {
    if (!attachRoleId) {
      setAttachError('Select a role.');
      return;
    }
    if (!attachPolicyId) {
      setAttachError('Select a policy to attach.');
      return;
    }
    const policy = policies.find((p) => p.id === attachPolicyId);
    if (!policy) {
      setAttachError('Selected policy not found.');
      return;
    }
    const role = roles.find((r) => r.id === attachRoleId);
    if (!role) {
      setAttachError('Selected role not found.');
      return;
    }
    setAttaching(true);
    setAttachError(null);
    try {
      const result = await abacPoliciesClient.POST(
        '/api/v1/admin/roles/{roleId}/abac-policies',
        {
          params: { path: { roleId: attachRoleId } },
          body: { abacPolicyId: attachPolicyId },
        },
      );
      if (result.error) {
        setAttachError(formatApiError(result, 'Failed to attach policy to role'));
        return;
      }
      // Server returns 204 with no body. Track the attachment in local state
      // so the Role Attachments panel reflects the action; the canonical spec
      // does not expose a role-attachment GET (see file header).
      setAttachments((prev) => {
        const exists = prev.some(
          (a) => a.roleId === attachRoleId && a.policy.id === attachPolicyId,
        );
        if (exists) return prev;
        return [
          ...prev,
          { roleId: attachRoleId, roleName: role.displayName, policy },
        ];
      });
      setAttachPolicyId('');
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Failed to attach policy');
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = async (roleId: string, policyId: string) => {
    setError(null);
    try {
      const result = await abacPoliciesClient.DELETE(
        '/api/v1/admin/roles/{roleId}/abac-policies/{abacPolicyId}',
        { params: { path: { roleId, abacPolicyId: policyId } } },
      );
      if (result.error) {
        setError(formatApiError(result, 'Failed to detach policy'));
        return;
      }
      setAttachments((prev) =>
        prev.filter((a) => !(a.roleId === roleId && a.policy.id === policyId)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to detach policy');
    }
  };

  /**
   * Attach an ABAC policy as a per-user override on a specific UserRole
   * binding. Hits POST /api/v1/admin/staff/{userId}/role-abac-overrides
   * via the typed openapi-fetch client.
   *
   * The userId path param is the TenantUser row UUID; the body's
   * userRoleId is the UserRole join-row UUID. Neither id is surfaced by
   * the existing fi-staff-lifecycle staff-list endpoint, so this
   * surface validates UUIDs client-side and fires the request directly.
   * A follow-on spec extension can surface UserRole.id on the staff
   * list and replace these inputs with cascading Selects.
   */
  const handleAttachOverride = async () => {
    if (!overrideUserId.trim()) {
      setOverrideError('TenantUser ID is required.');
      return;
    }
    if (!UUID_RE.test(overrideUserId.trim())) {
      setOverrideError('TenantUser ID must be a UUID.');
      return;
    }
    if (!overrideUserRoleId.trim()) {
      setOverrideError('UserRole ID is required.');
      return;
    }
    if (!UUID_RE.test(overrideUserRoleId.trim())) {
      setOverrideError('UserRole ID must be a UUID.');
      return;
    }
    if (!overridePolicyId) {
      setOverrideError('Select a policy.');
      return;
    }
    const policy = policies.find((p) => p.id === overridePolicyId);
    if (!policy) {
      setOverrideError('Selected policy not found.');
      return;
    }
    setOverriding(true);
    setOverrideError(null);
    try {
      const result = await abacPoliciesClient.POST(
        '/api/v1/admin/staff/{userId}/role-abac-overrides',
        {
          params: { path: { userId: overrideUserId.trim() } },
          body: {
            userRoleId: overrideUserRoleId.trim(),
            abacPolicyId: overridePolicyId,
          },
        },
      );
      if (result.error) {
        setOverrideError(
          formatApiError(result, 'Failed to attach per-user override'),
        );
        return;
      }
      // Server returns 204 with no body. Track in session-local state so
      // the Per-User Overrides panel reflects the action.
      setOverrides((prev) => {
        const exists = prev.some(
          (o) =>
            o.userId === overrideUserId.trim() &&
            o.userRoleId === overrideUserRoleId.trim() &&
            o.policy.id === overridePolicyId,
        );
        if (exists) return prev;
        return [
          ...prev,
          {
            userId: overrideUserId.trim(),
            userRoleId: overrideUserRoleId.trim(),
            policy,
          },
        ];
      });
      setOverridePolicyId('');
    } catch (err) {
      setOverrideError(
        err instanceof Error ? err.message : 'Failed to attach per-user override',
      );
    } finally {
      setOverriding(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const policyOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'Select policy...' },
      ...policies.map((p) => ({ value: p.id, label: p.name })),
    ],
    [policies],
  );

  const roleOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'Select role...' },
      ...roles.map((r) => ({
        value: r.id,
        label: `${r.displayName} (${r.name})`,
      })),
    ],
    [roles],
  );

  const truncate = (value: string | null | undefined, max: number): string => {
    if (!value) return '';
    return value.length > max ? `${value.slice(0, max)}...` : value;
  };

  // DataTable prints an accessor's value as text; markup goes in `cell`.
  // Each column here returned its markup from `accessor`, so the table
  // printed "[object Object]" in every cell, Edit and Delete included.
  const columns: DataTableColumn<AbacPolicy>[] = [
    {
      header: 'Name',
      accessor: 'name',
      cell: (_value, row) => (
        <div className="text-sm font-medium text-primary">{row.name}</div>
      ),
    },
    {
      header: 'Description',
      accessor: 'description',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {truncate(row.description, 80) || '-'}
        </span>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: 'Updated',
      accessor: 'updatedAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {new Date(row.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      header: 'Actions',
      align: 'right',
      accessor: 'id',
      cell: (_value, row) => (
        <div className="flex justify-end gap-2">
          {canManage && !row.isSystem && (
            <>
              <Button variant="ghost" size="sm" onClick={() => openEditEditor(row)}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void handleDelete(row)}>
                Delete
              </Button>
            </>
          )}
          {row.isSystem && (
            <Badge variant="neutral" size="sm">
              System
            </Badge>
          )}
        </div>
      ),
    },
  ];

  // Session-local attachments, one table row each, ordered by role then
  // policy so a role's attachments read together.
  const sortedAttachments = useMemo(
    () =>
      [...attachments].sort(
        (a, b) =>
          a.roleName.localeCompare(b.roleName) ||
          a.policy.name.localeCompare(b.policy.name),
      ),
    [attachments],
  );

  const attachmentColumns: DataTableColumn<RoleAttachment>[] = [
    {
      header: 'Role',
      accessor: 'roleName',
      cell: (_value, row) => (
        <span className="text-sm font-medium text-primary">{row.roleName}</span>
      ),
    },
    {
      header: 'Policy',
      accessor: (row) => row.policy.name,
      cell: (_value, row) => (
        <span className="text-sm text-secondary">{row.policy.name}</span>
      ),
    },
    {
      header: 'Actions',
      align: 'right',
      accessor: 'roleId',
      cell: (_value, row) =>
        canManage ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleDetach(row.roleId, row.policy.id)}
          >
            Detach
          </Button>
        ) : null,
    },
  ];

  const overrideColumns: DataTableColumn<(typeof overrides)[number]>[] = [
    {
      header: 'Policy',
      accessor: (row) => row.policy.name,
      cell: (_value, row) => (
        <span className="text-sm font-medium text-primary">{row.policy.name}</span>
      ),
    },
    {
      header: 'TenantUser ID',
      accessor: 'userId',
      cell: (_value, row) => (
        <span className="text-xs font-mono text-secondary">{row.userId}</span>
      ),
    },
    {
      header: 'UserRole ID',
      accessor: 'userRoleId',
      cell: (_value, row) => (
        <span className="text-xs font-mono text-secondary">{row.userRoleId}</span>
      ),
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  //
  // Layout (GH #679): three sections on the canvas, each a serif heading,
  // its explanation and its controls, separated by spacing rather than
  // wrapped in cards. DataTable carries its own card, so a table is never
  // nested in a second one. Form fields sit in a top-aligned grid with the
  // action button on its own row, so a field's helper text cannot push the
  // button out of line. The policy editor is a side sheet holding the
  // predicate and its preview in one scroll, with no tabs between them.
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div role="status" aria-label="Loading ABAC policies">
        <SkeletonTable rows={4} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <BodyText variant="ui" size="sm" color="tertiary">
            Author attribute-based access control policies and attach them to
            roles or to one staff member&apos;s role. A policy&apos;s predicate
            uses a fixed subset of JsonLogic operators, listed in the editor.
          </BodyText>
          {canManage && (
            <Button variant="primary" onClick={openCreateEditor}>
              Create Policy
            </Button>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <Alert type="error">{error}</Alert>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        )}
      </div>

      {/* Policy list */}
      <section aria-labelledby="abac-policies-heading" className="space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <Heading level={2} size="subsection" id="abac-policies-heading">
            Policies
          </Heading>
          <span className="text-sm font-sans text-tertiary">
            {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
          </span>
        </div>
        {policies.length === 0 ? (
          <EmptyState
            title="No ABAC policies yet"
            description="A policy limits what a role may do by the attributes of the request, such as the hour, the loan amount or the branch."
          />
        ) : (
          <DataTable columns={columns} data={policies} rowKey="id" hoverable striped />
        )}
      </section>

      {/* Attach-to-role section */}
      <section aria-labelledby="abac-role-attachments-heading" className="space-y-4">
        <div className="space-y-1">
          <Heading level={2} size="subsection" id="abac-role-attachments-heading">
            Role Attachments
          </Heading>
          <BodyText size="sm" color="secondary">
            Attach policies to roles. A staff member&apos;s effective policy
            set is the union of the policies attached to every role they hold.
          </BodyText>
        </div>

        {canManage && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Select
                label="Role"
                options={roleOptions}
                value={attachRoleId}
                onChange={(e) => setAttachRoleId(e.target.value)}
                disabled={roles.length === 0}
                helperText={
                  roles.length === 0 ? 'No roles available.' : undefined
                }
              />
              <Select
                id="abac-attach-policy"
                label="Policy"
                options={policyOptions}
                value={attachPolicyId}
                onChange={(e) => setAttachPolicyId(e.target.value)}
              />
            </div>
            {attachError && (
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Alert type="error">{attachError}</Alert>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAttachError(null)}
                >
                  Dismiss
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={() => void handleAttach()}
                disabled={attaching || !attachRoleId || !attachPolicyId}
              >
                {attaching ? 'Attaching...' : 'Attach'}
              </Button>
            </div>
          </div>
        )}

        {sortedAttachments.length === 0 ? (
          <BodyText variant="ui" size="sm" color="tertiary">
            No attachments made on this visit. This list shows the attachments
            made since the page loaded; attachments made earlier stay in force
            and are not listed here.
          </BodyText>
        ) : (
          <DataTable
            columns={attachmentColumns}
            data={sortedAttachments}
            rowKey={(row) => `${row.roleId}-${row.policy.id}`}
          />
        )}
      </section>

      {/* Per-user override section (POST /api/v1/admin/staff/{userId}/role-abac-overrides) */}
      <section aria-labelledby="abac-overrides-heading" className="space-y-4">
        <div className="space-y-1">
          <Heading level={2} size="subsection" id="abac-overrides-heading">
            Per-User Policy Overrides
          </Heading>
          <BodyText size="sm" color="secondary">
            Attach a policy to one staff member&apos;s role, on top of the
            policies attached to the role itself. Until the staff list shows
            role assignment IDs, enter the TenantUser ID and the UserRole ID
            as UUIDs.
          </BodyText>
        </div>

        {canManage && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Input
                label="TenantUser ID"
                value={overrideUserId}
                onChange={(e) => setOverrideUserId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                helperText="TenantUser row UUID for the staff member."
              />
              <Input
                label="UserRole ID"
                value={overrideUserRoleId}
                onChange={(e) => setOverrideUserRoleId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                helperText="UserRole join-row UUID. Must belong to the user above."
              />
              <Select
                id="abac-override-policy"
                label="Policy"
                options={policyOptions}
                value={overridePolicyId}
                onChange={(e) => setOverridePolicyId(e.target.value)}
              />
            </div>
            {overrideError && (
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Alert type="error">{overrideError}</Alert>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOverrideError(null)}
                >
                  Dismiss
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={() => void handleAttachOverride()}
                disabled={
                  overriding ||
                  !overrideUserId ||
                  !overrideUserRoleId ||
                  !overridePolicyId
                }
              >
                {overriding ? 'Attaching...' : 'Attach Override'}
              </Button>
            </div>
          </div>
        )}

        {overrides.length === 0 ? (
          <BodyText variant="ui" size="sm" color="tertiary">
            No overrides made on this visit. This list shows the overrides made
            since the page loaded; overrides made earlier stay in force and are
            not listed here.
          </BodyText>
        ) : (
          <DataTable
            columns={overrideColumns}
            data={overrides}
            rowKey={(row) => `${row.userId}-${row.userRoleId}-${row.policy.id}`}
          />
        )}
      </section>

      {/* Policy editor: a side sheet over the list */}
      <Sheet
        open={editorOpen}
        onOpenChange={(o) => {
          if (!o) closeEditor();
        }}
        title={editTarget ? `Edit Policy: ${editTarget.name}` : 'Create Policy'}
        side="right"
        size="xl"
        isDismissable={false}
        footer={
          <>
            <Button variant="ghost" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              disabled={saving || !isFormValid()}
            >
              {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Create Policy'}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {saveError && (
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <Alert type="error">{saveError}</Alert>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSaveError(null)}
              >
                Dismiss
              </Button>
            </div>
          )}

          <div className="space-y-4">
            {/* Name (locked on edit per Sprint 1 deviation #3 -- name doubles as slug) */}
            <Input
              label="Name"
              value={editTarget ? editTarget.name : formName}
              onChange={(e) => setFormName(e.target.value)}
              disabled={!!editTarget}
              required={!editTarget}
              maxLength={255}
              helperText={
                editTarget
                  ? 'Name is immutable. It serves as both slug and label.'
                  : 'Doubles as slug and display label. Cannot be changed after creation.'
              }
            />

            <Textarea
              label="Description"
              value={formDescription}
              onChange={(value) => setFormDescription(value)}
              maxLength={1000}
              showCharCount
              rows={2}
            />

            <Textarea
              label="Predicate (JsonLogic, locked subset)"
              value={predicateText}
              onChange={(value) => {
                setPredicateText(value);
                setPredicateError(null);
              }}
              onBlur={handlePredicateBlur}
              rows={12}
              className="font-mono text-sm"
              error={predicateError ?? undefined}
              required
              helperText={
                'Permitted operators: ==, !=, <, <=, >, >=, all, any, not, in, var.'
              }
            />
          </div>

          {/* Preview sits under the predicate so both stay in view */}
          <section
            aria-labelledby="abac-preview-label"
            className="space-y-3 border-t border-recessed pt-6"
          >
            <SectionLabel id="abac-preview-label">Preview</SectionLabel>
            <Select
              label="Sample context preset"
              options={[
                { value: '', label: 'Select a preset...' },
                ...CONTEXT_PRESETS.map((p) => ({
                  value: p.id,
                  label: p.label,
                })),
              ]}
              onChange={(e) => applyContextPreset(e.target.value)}
            />
            <Textarea
              label="Sample context (JSON object)"
              value={contextText}
              onChange={(value) => {
                setContextText(value);
                setContextError(null);
                setPreviewResult(null);
              }}
              rows={8}
              className="font-mono text-sm"
              error={contextError ?? undefined}
            />
            <div>
              <Button variant="secondary" onClick={handleRunPreview}>
                Run Check
              </Button>
            </div>
            {/* A persistent live region: a status mounted with its content
                is often not announced (see the Alert primitive's role note). */}
            <div role="status" aria-live="polite">
              {previewResult && (
                <Alert
                  type={previewResult.allowed ? 'success' : 'warning'}
                  title={previewResult.allowed ? 'ALLOWED' : 'DENIED'}
                >
                  <span>
                    {previewResult.reason ??
                      (previewResult.allowed
                        ? 'Predicate evaluates to true.'
                        : 'Predicate evaluates to false.')}
                  </span>
                  <span className="mt-2 block text-xs text-tertiary">
                    Client-side preview. Production evaluation runs server-side
                    at /api/v1/abac/check against the caller&apos;s effective policies.
                  </span>
                </Alert>
              )}
            </div>
          </section>
        </div>
      </Sheet>
    </div>
  );
}
